import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import {
  deleteB2BOrder,
  exportB2BOrders,
  listB2BOrders,
} from '../../services/shipturtleApi';
import { showToast } from '../../utils/toast';
import { b2bOrderStatusClass } from './b2bStatusBadge';
import B2BConfirmModal from './B2BConfirmModal';
import B2BOrderDetail from './B2BOrderDetail';
import B2BShipmentModal from './B2BShipmentModal';
import { openB2BDocument } from './b2bDocuments';
import './B2BOrders.css';

/**
 * Bulk/institutional orders.
 *
 * Rows are whole orders raised by the B2B team; the shipment ops books against
 * one is a child row, so an order can legitimately sit here with no AWB at all.
 * "Awaiting shipment" is a real state, not missing data.
 *
 * The row opens the order as a modal rather than navigating away, and the
 * shipment form is reachable straight from the row — adding an AWB is the most
 * common thing ops does, and it should not require opening the order first.
 */
const PAGE_SIZE = 25;

const FILTER_LABELS = {
  vendor: 'Vendor',
  mode: 'Mode',
  status: 'Status',
  has_shipment: 'Shipment',
  date_from: 'Created from',
  date_to: 'Created to',
};

const STATUS_OPTIONS = [
  ['order_placed', 'Order placed'],
  ['picked_up', 'Picked up'],
  ['in_transit', 'In transit'],
  ['out_for_delivery', 'Out for delivery'],
  ['delivered', 'Delivered'],
  ['rto', 'RTO'],
  ['shipment_delayed', 'Delayed'],
];

/**
 * The row's ⋯ menu.
 *
 * Rendered through a portal onto <body>, not inside the row: the table sits in
 * an `overflow-x: auto` container, and an overflow container clips absolutely
 * positioned descendants — so a menu positioned inside the row would be
 * cropped. Fixed coordinates off the trigger's viewport rect escape that, the
 * same way the manual-orders list does it.
 */
function RowMenu({
  row,
  onDetails,
  onDocuments,
  onEdit,
  onAddShipment,
  onEditShipment,
  onDelete,
}) {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState(null);
  const ref = useRef(null);
  const menuRef = useRef(null);

  const place = useCallback(() => {
    const trigger = ref.current;
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    // Measured once rendered; the estimate only covers the first placement.
    // The item count varies with the order's shipments and documents.
    const MENU_H = menuRef.current?.offsetHeight || 340;
    // Flip above when there is no room below, so the last row's menu is never
    // pinned off-screen.
    const openUp = rect.bottom + MENU_H > window.innerHeight && rect.top > MENU_H;
    setCoords({
      top: openUp ? rect.top - MENU_H - 4 : rect.bottom + 4,
      right: Math.max(8, window.innerWidth - rect.right),
    });
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    place();
    const onDocClick = (e) => {
      if (
        ref.current &&
        !ref.current.contains(e.target) &&
        menuRef.current &&
        !menuRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    // A scroll or resize moves the trigger, so the menu has to follow.
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [open, place]);

  return (
    <div className="b2bRowMenu" ref={ref}>
      <button
        type="button"
        className="b2bRowMenuTrigger"
        aria-label={`Actions for ${row.order_number}`}
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        ⋯
      </button>
      {open &&
        coords &&
        createPortal(
          <div
            className="b2bRowMenuList"
            role="menu"
            ref={menuRef}
            style={{ top: coords.top, right: coords.right }}
          >
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                onDetails(row);
              }}
            >
              View details
            </button>
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                onEdit(row);
              }}
            >
              Edit order
            </button>
            {/* Downloads open straight from the row; absent documents are
                listed but disabled, so the menu's shape never shifts and it is
                plain which one is missing. */}
            <div className="b2bRowMenuGroup" role="presentation">
              <button
                type="button"
                role="menuitem"
                disabled={!row.invoice_document_url}
                title={row.invoice_document_url ? undefined : 'No invoice uploaded'}
                onClick={() => {
                  setOpen(false);
                  openB2BDocument(row.invoice_document_url);
                }}
              >
                Download invoice
              </button>
              <button
                type="button"
                role="menuitem"
                disabled={!row.eway_bill_document_url}
                title={row.eway_bill_document_url ? undefined : 'No e-way bill attached'}
                onClick={() => {
                  setOpen(false);
                  openB2BDocument(row.eway_bill_document_url);
                }}
              >
                Download e-way bill
              </button>
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setOpen(false);
                  onDocuments(row);
                }}
              >
                View all documents
              </button>
            </div>
            {/* One slot, two meanings: an order with no consignment needs one
                added, and an order with exactly one is almost always being
                corrected rather than extended. */}
            {row.shipment_count === 0 ? (
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setOpen(false);
                  onAddShipment(row);
                }}
              >
                Add shipment
              </button>
            ) : (
              <>
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setOpen(false);
                    onEditShipment(row);
                  }}
                >
                  {row.shipment_count === 1
                    ? 'Edit shipment & status'
                    : 'Edit shipments & status'}
                </button>
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setOpen(false);
                    onAddShipment(row);
                  }}
                >
                  Add another shipment
                </button>
              </>
            )}
            {/* Last and set apart, so it is never the item hit by accident. */}
            <button
              type="button"
              role="menuitem"
              className="isDanger"
              onClick={() => {
                setOpen(false);
                onDelete(row);
              }}
            >
              Delete order
            </button>
          </div>,
          document.body
        )}
    </div>
  );
}

const money = (value) =>
  value === null || value === undefined || value === ''
    ? '—'
    : `₹${Number(value).toLocaleString('en-IN', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;

export default function B2BOrdersTable() {
  const navigate = useNavigate();

  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [hasShipment, setHasShipment] = useState('');
  const [filters, setFilters] = useState({});
  const [showFilters, setShowFilters] = useState(false);
  const [draft, setDraft] = useState({});

  const [selected, setSelected] = useState([]);
  // Distinct from `selected`: "every row matching the current filters",
  // including pages never loaded. The export sends the filters so the server
  // re-runs them rather than trusting a list of ids.
  const [selectAllMode, setSelectAllMode] = useState(false);
  const [exporting, setExporting] = useState(false);

  const [detailId, setDetailId] = useState(null);
  // The tab the detail opens on — Documents when reached from the row's menu.
  const [detailTab, setDetailTab] = useState('details');
  const [shipmentFor, setShipmentFor] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  // { order, shipment } — shipment null when adding.
  const [editShipment, setEditShipment] = useState(null);

  const queryParams = useCallback(
    () => ({
      q: search.trim(),
      status,
      has_shipment: hasShipment,
      ...filters,
    }),
    [search, status, hasShipment, filters]
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await listB2BOrders({
        ...queryParams(),
        limit: PAGE_SIZE,
        offset,
      });
      setRows(data?.orders || []);
      setCount(data?.count || 0);
    } catch (err) {
      setError(err?.message || 'Could not load B2B orders.');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [queryParams, offset]);

  // Debounced so typing in the search box does not fire a request per keystroke.
  useEffect(() => {
    const timer = setTimeout(load, 300);
    return () => clearTimeout(timer);
  }, [load]);

  // A changed filter or search means the previous selection refers to rows that
  // may no longer be listed, so it is dropped rather than silently exporting
  // something the admin can no longer see.
  useEffect(() => {
    setSelected([]);
    setSelectAllMode(false);
  }, [search, status, hasShipment, filters]);

  const page = Math.floor(offset / PAGE_SIZE) + 1;
  const pages = Math.max(1, Math.ceil(count / PAGE_SIZE));
  const activeFilters = Object.entries(filters).filter(([, v]) => v !== '' && v != null);

  const pageIds = rows.map((r) => r.id);
  const allOnPageSelected = rows.length > 0 && pageIds.every((id) => selected.includes(id));
  const selectionCount = selectAllMode ? count : selected.length;

  const toggleRow = (id) => {
    setSelectAllMode(false);
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const toggleAll = () => {
    setSelectAllMode(false);
    setSelected(allOnPageSelected ? [] : pageIds);
  };

  const clearSelection = () => {
    setSelected([]);
    setSelectAllMode(false);
  };

  const applyFilters = () => {
    const clean = {};
    Object.entries(draft).forEach(([key, value]) => {
      if (Array.isArray(value) ? value.length : value !== '' && value != null) {
        clean[key] = Array.isArray(value) ? value.join(',') : value;
      }
    });
    setOffset(0);
    setFilters(clean);
    setShowFilters(false);
  };

  /**
   * Delete an order.
   *
   * Soft server-side: the row, its vendors, boxes, shipments and change log all
   * survive and the order simply stops appearing. The confirmation says so, so
   * nobody hesitates over a button they think is destructive — or treats it as
   * harmless when it removes the order from every export.
   */
  const handleDelete = async (reason) => {
    const row = deleteTarget;
    if (!row) return;

    setDeleting(true);
    try {
      await deleteB2BOrder(row.id, reason);
      showToast({ type: 'success', message: `${row.order_number} deleted.` });
      // Drop it from the selection too, or an export would carry an id the
      // server will no longer return.
      setSelected((prev) => prev.filter((id) => id !== row.id));
      setDeleteTarget(null);
      load();
    } catch (err) {
      showToast({ type: 'error', message: err?.message || 'Could not delete it.' });
    } finally {
      setDeleting(false);
    }
  };

  /**
   * Download the current view.
   *
   * Three shapes, one call: ticked rows export exactly those ids; select-all
   * mode and an untouched selection both export the whole filtered view, since
   * that is what is on screen.
   */
  const handleExport = async () => {
    setExporting(true);
    try {
      const useSelectAll = selectAllMode || selected.length === 0;
      const blob = await exportB2BOrders({
        ...queryParams(),
        ids: useSelectAll ? '' : selected.join(','),
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `b2b_orders_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      showToast({ type: 'success', message: 'Export downloaded.' });
    } catch (err) {
      showToast({ type: 'error', message: err?.message || 'Could not export.' });
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="b2bWrap b2bPage">
      <div className="b2bPageHead">
        <h1 className="b2bPageTitle">B2B Orders</h1>
        <button
          type="button"
          className="b2bNewBtn"
          onClick={() => navigate('/suspire-terminal/b2b-orders/new')}
        >
          + New B2B Order
        </button>
      </div>

      <div className="b2bToolbar">
        <div className="b2bToolbarLeft">
        <input
          className="b2bSearch"
          placeholder="Search order, invoice, vendor, customer or AWB…"
          value={search}
          onChange={(e) => {
            setOffset(0);
            setSearch(e.target.value);
          }}
        />
        <select
          className="b2bSelect"
          value={status}
          onChange={(e) => {
            setStatus(e.target.value);
            setOffset(0);
          }}
        >
          <option value="">All statuses</option>
          {STATUS_OPTIONS.map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
        <select
          className="b2bSelect"
          value={hasShipment}
          onChange={(e) => {
            setHasShipment(e.target.value);
            setOffset(0);
          }}
        >
          <option value="">All orders</option>
          <option value="false">Awaiting shipment</option>
          <option value="true">Has a shipment</option>
        </select>

        </div>

        <div className="b2bToolbarRight">
          <button
            type="button"
            className={`b2bToolbarBtn ${activeFilters.length ? 'hasActive' : ''}`}
            onClick={() => {
              setDraft(filters);
              setShowFilters(true);
            }}
          >
            Filters
            {activeFilters.length > 0 && (
              <span className="b2bFilterBadge">{activeFilters.length}</span>
            )}
          </button>
          <button
            type="button"
            className="b2bExportBtn"
            onClick={handleExport}
            disabled={exporting || count === 0}
          >
            {exporting
              ? 'Exporting…'
              : selectionCount > 0 && !selectAllMode
                ? `Export ${selectionCount}`
                : 'Export'}
          </button>
        </div>
      </div>

      {!loading && (
        <div className="b2bResultsSummary">
          {count === 0 ? (
            'No orders match these filters.'
          ) : (
            <>
              Showing <strong>{offset + 1}–{Math.min(offset + rows.length, count)}</strong> of{' '}
              <strong>{count}</strong> order{count === 1 ? '' : 's'}
              {selectionCount > 0 ? ` · ${selectionCount} selected` : ''}
            </>
          )}
        </div>
      )}

      {/* Offers the rows the admin cannot see. Without it "select all" silently
          means "this page only", which is the wrong answer for an export. */}
      {allOnPageSelected && count > rows.length && (
        <div className="b2bSelectionBanner">
          {selectAllMode ? (
            <>
              <span>
                All <strong>{count}</strong> matching orders are selected.
              </span>
              <button type="button" onClick={clearSelection}>
                Clear selection
              </button>
            </>
          ) : (
            <>
              <span>
                All <strong>{rows.length}</strong> on this page are selected.
              </span>
              <button type="button" onClick={() => setSelectAllMode(true)}>
                Select all {count} matching orders
              </button>
            </>
          )}
        </div>
      )}

      {/* Applied filters as removable chips, so what is narrowing the list is
          visible without reopening the modal. */}
      {activeFilters.length > 0 && (
        <div className="b2bChips">
          {activeFilters.map(([key, value]) => (
            <span key={key} className="b2bChip">
              {FILTER_LABELS[key] || key}: {String(value).replace(/,/g, ', ')}
              <button
                type="button"
                onClick={() => {
                  const next = { ...filters };
                  delete next[key];
                  setOffset(0);
                  setFilters(next);
                }}
                aria-label={`Remove ${FILTER_LABELS[key] || key} filter`}
              >
                ×
              </button>
            </span>
          ))}
          <button
            type="button"
            className="b2bClearChips"
            onClick={() => {
              setOffset(0);
              setFilters({});
            }}
          >
            Clear all
          </button>
        </div>
      )}

      {error && <p className="b2bError">{error}</p>}

      <div className="b2bTableScroll">
        <table className="b2bTable">
          <thead>
            <tr>
              <th className="b2bCheckCol">
                <input
                  type="checkbox"
                  checked={allOnPageSelected}
                  onChange={toggleAll}
                  aria-label="Select all rows on this page"
                />
              </th>
              <th className="b2bColOrder">Order</th>
              <th className="b2bColVendors">Vendors &amp; products</th>
              <th className="b2bColInvoice">Invoice</th>
              <th className="b2bColMoney">Value</th>
              <th className="b2bColCustomer">Deliver to</th>
              <th className="b2bColAwb">Shipment</th>
              <th className="b2bColStatus">Status</th>
              <th className="b2bColActions">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={9} className="b2bEmpty">
                  Loading…
                </td>
              </tr>
            )}
            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan={9} className="b2bEmpty">
                  No B2B orders yet. Use <strong>+ New B2B Order</strong> to add one.
                </td>
              </tr>
            )}
            {!loading &&
              rows.map((row) => (
                <tr
                  key={row.id}
                  className={`b2bRow ${
                    selectAllMode || selected.includes(row.id) ? 'isSelected' : ''
                  }`}
                  onClick={() => {
                    setDetailTab('details');
                    setDetailId(row.id);
                  }}
                >
                  <td className="b2bCheckCol" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={selectAllMode || selected.includes(row.id)}
                      onChange={() => toggleRow(row.id)}
                      aria-label={`Select ${row.order_number}`}
                    />
                  </td>

                  <td className="b2bColOrder">
                    <strong>{row.order_number}</strong>
                    <span className="b2bSub">{row.order_date || '—'}</span>
                    {row.mode && <span className="b2bSub">{row.mode}</span>}
                  </td>

                  <td className="b2bColVendors">
                    {(row.vendor_names || []).length ? (
                      <>
                        <span className="b2bCellMain">{row.vendor_names[0]}</span>
                        {row.vendor_names.length > 1 && (
                          <span className="b2bSub">
                            +{row.vendor_names.length - 1} more vendor
                            {row.vendor_names.length > 2 ? 's' : ''}
                          </span>
                        )}
                      </>
                    ) : (
                      <span className="b2bCellMain">—</span>
                    )}
                    <span className="b2bSub">
                      {row.total_quantity ?? 0} units
                      {row.box_count ? ` · ${row.box_count} boxes` : ''}
                    </span>
                  </td>

                  <td className="b2bColInvoice">
                    <span className="b2bCellMain">{row.invoice_number || '—'}</span>
                    <span className="b2bSub">{row.invoice_date || '—'}</span>
                  </td>

                  <td className="b2bColMoney">
                    <span className="b2bCellMain">{money(row.invoice_value)}</span>
                  </td>

                  <td className="b2bColCustomer">
                    <span className="b2bCellMain">{row.customer_name || '—'}</span>
                    <span className="b2bSub">
                      {[row.delivery_city, row.delivery_pincode].filter(Boolean).join(' · ') ||
                        '—'}
                    </span>
                  </td>

                  <td className="b2bColAwb" onClick={(e) => e.stopPropagation()}>
                    {row.shipment_count ? (
                      <>
                        {/* The AWB is the tracking link: it is the thing ops
                            clicks, so it should not need a second control. */}
                        {row.latest_tracking_link ? (
                          <a
                            className="b2bAwbLink"
                            href={row.latest_tracking_link}
                            target="_blank"
                            rel="noreferrer"
                          >
                            {row.latest_awb || 'Track'}
                          </a>
                        ) : (
                          <span className="b2bCellMain">{row.latest_awb || '—'}</span>
                        )}
                        <span className="b2bSub">
                          {row.latest_shipper || ''}
                          {row.shipment_count > 1
                            ? ` · +${row.shipment_count - 1} more`
                            : ''}
                        </span>
                      </>
                    ) : (
                      <button
                        type="button"
                        className="b2bAddShipmentInline"
                        onClick={() => setShipmentFor(row)}
                      >
                        + Add shipment
                      </button>
                    )}
                  </td>

                  <td className="b2bColStatus">
                    <span className={`b2bStatusBadge ${b2bOrderStatusClass(row)}`}>
                      {row.derived_status_label}
                    </span>
                  </td>

                  <td className="b2bColActions" onClick={(e) => e.stopPropagation()}>
                    <RowMenu
                      row={row}
                      onDetails={(r) => {
                        setDetailTab('details');
                        setDetailId(r.id);
                      }}
                      onDocuments={(r) => {
                        setDetailTab('documents');
                        setDetailId(r.id);
                      }}
                      onEdit={(r) =>
                        navigate(`/suspire-terminal/b2b-orders/${r.id}/edit`)
                      }
                      onAddShipment={(r) => setShipmentFor(r)}
                      onDelete={(r) => setDeleteTarget(r)}
                      onEditShipment={(r) => {
                        // One consignment can be edited straight from the row.
                        // Several need choosing between, which is what the
                        // detail view's Shipments tab is for.
                        if (r.shipment_count === 1) {
                          setEditShipment(r);
                        } else {
                          setDetailTab('shipments');
                          setDetailId(r.id);
                        }
                      }}
                    />
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {pages > 1 && (
        <div className="b2bPager">
          <button
            type="button"
            disabled={offset === 0}
            onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
          >
            Previous
          </button>
          <span>
            Page {page} of {pages}
          </span>
          <button
            type="button"
            disabled={offset + PAGE_SIZE >= count}
            onClick={() => setOffset(offset + PAGE_SIZE)}
          >
            Next
          </button>
        </div>
      )}

      {showFilters && (
        <div className="b2bModalBackdrop" onClick={() => setShowFilters(false)}>
          <div
            className="b2bModalPanel b2bModalNarrow"
            onClick={(e) => e.stopPropagation()}
          >
            <header className="b2bModalHead">
              <h3>Filter B2B orders</h3>
              <button
                type="button"
                className="b2bModalClose"
                onClick={() => setShowFilters(false)}
                aria-label="Close"
              >
                ×
              </button>
            </header>
            <div className="b2bModalBody">
              <div className="b2bFormGrid">
                <label className="field">
                  <span>Vendor</span>
                  <input
                    value={draft.vendor || ''}
                    onChange={(e) => setDraft({ ...draft, vendor: e.target.value })}
                    placeholder="Any part of the name"
                  />
                </label>
                <label className="field">
                  <span>Mode</span>
                  <input
                    value={draft.mode || ''}
                    onChange={(e) => setDraft({ ...draft, mode: e.target.value })}
                    placeholder="e.g. Prozo-PTL"
                  />
                </label>
              </div>
              <div className="b2bFormGrid">
                <label className="field">
                  <span>Created from</span>
                  <input
                    type="date"
                    value={draft.date_from || ''}
                    onChange={(e) => setDraft({ ...draft, date_from: e.target.value })}
                  />
                </label>
                <label className="field">
                  <span>Created to</span>
                  <input
                    type="date"
                    value={draft.date_to || ''}
                    onChange={(e) => setDraft({ ...draft, date_to: e.target.value })}
                  />
                </label>
              </div>
            </div>
            <footer className="b2bModalFoot">
              <button type="button" className="btn btnGhost" onClick={() => setDraft({})}>
                Reset
              </button>
              <button type="button" className="btn btnPrimary" onClick={applyFilters}>
                Apply
              </button>
            </footer>
          </div>
        </div>
      )}

      {detailId && (
        <B2BOrderDetail
          orderId={detailId}
          initialTab={detailTab}
          onClose={() => setDetailId(null)}
          onChanged={load}
        />
      )}

      {shipmentFor && (
        <B2BShipmentModal
          order={shipmentFor}
          onClose={() => setShipmentFor(null)}
          onSaved={load}
        />
      )}

      {deleteTarget && (
        <B2BConfirmModal
          title={`Delete ${deleteTarget.order_number}?`}
          body="It disappears from this list and from exports. The record is kept, so an admin can still see or restore it."
          confirmLabel="Delete order"
          danger
          withReason
          busy={deleting}
          onConfirm={handleDelete}
          onClose={() => setDeleteTarget(null)}
        />
      )}

      {editShipment && (
        <B2BShipmentModal
          order={editShipment}
          editOnly
          onClose={() => setEditShipment(null)}
          onSaved={load}
        />
      )}
    </div>
  );
}
